import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Trophy, BarChart3, MessageCircle, Crown } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useBalance } from '../../hooks/usePortfolio';
import { useAccessControl } from '../../contexts/AccessControlContext';
import { useLayoutVariant } from '../../hooks/useLayoutVariant';

interface DesktopToolbeltProps {
  onLeaderboardClick: () => void;
  onStatsClick: () => void;
  onDiscussionClick: () => void;
  onPremiumClick: () => void;
  showMemberNotificationDot?: boolean;
}

interface ToolbeltButtonProps {
  icon: React.ReactNode;
  label: string;
  sublabel?: string;
  onClick: () => void;
  badge?: string;
  badgeVariant?: 'green' | 'purple';
  notificationDot?: boolean;
  ariaLabel: string;
}

const ToolbeltButton: React.FC<ToolbeltButtonProps> = ({
  icon,
  label,
  sublabel,
  onClick,
  badge,
  badgeVariant = 'green',
  notificationDot,
  ariaLabel
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative flex flex-col items-center justify-center gap-0.5",
        "px-3 py-1.5 rounded-lg",
        "transition-all duration-200 ease-in-out",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        "hover:scale-105 hover:bg-gray-200/50 dark:hover:bg-gray-700/50",
        "active:scale-95",
        "focus:ring-gray-400 focus:ring-offset-transparent"
      )}
      aria-label={ariaLabel}
    >
      {badge && (
        <span className={cn(
          "absolute -top-1 -right-1 inline-flex items-center justify-center rounded px-1.5 py-0.5 text-[0.65rem] font-semibold text-white leading-none",
          badgeVariant === 'purple'
            ? "bg-purple-600 dark:bg-purple-500"
            : "bg-green-500 dark:bg-green-400 dark:text-green-900"
        )}>
          {badge}
        </span>
      )}
      <span className="relative text-gray-700 dark:text-gray-300">
        {icon}
        {notificationDot && (
          <span className="absolute -top-2 -right-2 w-2.5 h-2.5 rounded-full bg-blue-500" />
        )}
      </span>
      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
        {label}
      </span>
      {sublabel && (
        <span className="text-[0.65rem] font-medium tabular-nums text-muted-foreground leading-tight">
          {sublabel}
        </span>
      )}
    </button>
  );
};

const MobileToolbeltButton: React.FC<ToolbeltButtonProps> = ({
  icon,
  label,
  sublabel,
  onClick,
  badge,
  badgeVariant = 'green',
  notificationDot,
  ariaLabel
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "relative flex flex-col items-center justify-center gap-0",
        "px-2 py-1 rounded-md",
        "transition-all duration-200 ease-in-out",
        "focus:outline-none",
        "active:scale-95"
      )}
      aria-label={ariaLabel}
    >
      {badge && (
        <span className={cn(
          "absolute -top-1 -right-1.5 inline-flex items-center justify-center rounded px-1 py-0 text-[0.5rem] font-semibold text-white leading-none",
          badgeVariant === 'purple'
            ? "bg-purple-600 dark:bg-purple-500"
            : "bg-green-500 dark:bg-green-400 dark:text-green-900"
        )}>
          {badge}
        </span>
      )}
      <span className="relative text-gray-700 dark:text-gray-300">
        {icon}
        {notificationDot && (
          <span className="absolute -top-2 -right-2 w-2.5 h-2.5 rounded-full bg-blue-500" />
        )}
      </span>
      <span className="text-[0.6rem] font-medium text-gray-700 dark:text-gray-300 leading-tight">
        {label}
      </span>
      {sublabel && (
        <span className="text-[0.5rem] font-medium tabular-nums text-muted-foreground leading-tight">
          {sublabel}
        </span>
      )}
    </button>
  );
};

export const DesktopToolbelt: React.FC<DesktopToolbeltProps> = ({
  onLeaderboardClick,
  onStatsClick,
  onDiscussionClick,
  onPremiumClick,
  showMemberNotificationDot
}) => {
  const navigate = useNavigate();
  const { data: balance } = useBalance();
  const { shouldShowPremiumButton } = useAccessControl();
  const { isStreamerMode, isActuallyDesktop } = useLayoutVariant();
  const isStreamerDesktop = isStreamerMode && isActuallyDesktop;
  const coinSublabel = `$${(balance?.balanceCoins || 0).toLocaleString()}`;
  return (
    <>
      {/* Desktop toolbelt - floating pill */}
      <div
        className={cn(
          "hidden lg:flex",
          "fixed bottom-6 left-1/2 -translate-x-1/2",
          "rounded-full px-4 py-2 gap-2",
          "bg-white/80 dark:bg-gray-900/80 backdrop-blur-md",
          "border border-gray-200 dark:border-gray-700",
          "shadow-lg z-40",
          isStreamerDesktop && "scale-[0.8] origin-bottom"
        )}
        role="toolbar"
        aria-label="Game tools"
      >
        <ToolbeltButton
          icon={<Wallet className="w-5 h-5" />}
          label="Coins"
          sublabel={coinSublabel}
          onClick={() => navigate('/portfolio')}
          badge="BETA"
          ariaLabel="Go to paper portfolio"
        />
        <ToolbeltButton
          icon={<Trophy className="w-5 h-5" />}
          label="Leaderboard"
          onClick={onLeaderboardClick}
          ariaLabel="Open leaderboard modal"
        />
        <ToolbeltButton
          icon={<BarChart3 className="w-5 h-5" />}
          label="Stats"
          onClick={onStatsClick}
          ariaLabel="Open statistics modal"
        />
        <ToolbeltButton
          icon={<MessageCircle className="w-5 h-5" />}
          label="Discussion"
          onClick={onDiscussionClick}
          ariaLabel="Open discussion modal"
        />
        {shouldShowPremiumButton && (
          <ToolbeltButton
            icon={<Crown className="w-5 h-5 text-amber-500 dark:text-amber-400" />}
            label="Member"
            onClick={onPremiumClick}
            badge={undefined}
            badgeVariant="purple"
            notificationDot={showMemberNotificationDot}
            ariaLabel="Open member modal"
          />
        )}
      </div>

      {/* Mobile toolbelt - full-width bar at bottom */}
      <div
        className={cn(
          "flex lg:hidden",
          "fixed bottom-0 left-0 right-0",
          "justify-center gap-1 px-2 py-1.5",
          "bg-white dark:bg-gray-900 backdrop-blur-md",
          "border-t border-gray-300 dark:border-gray-700",
          "shadow-lg z-50"
        )}
        style={{ paddingBottom: 'max(0.375rem, env(safe-area-inset-bottom))' }}
        role="toolbar"
        aria-label="Game tools"
      >
        <MobileToolbeltButton
          icon={<Wallet className="w-4 h-4" />}
          label="Coins"
          sublabel={coinSublabel}
          onClick={() => navigate('/portfolio')}
          badge="BETA"
          ariaLabel="Go to paper portfolio"
        />
        <MobileToolbeltButton
          icon={<Trophy className="w-4 h-4" />}
          label="Leaderboard"
          onClick={onLeaderboardClick}
          ariaLabel="Open leaderboard modal"
        />
        <MobileToolbeltButton
          icon={<BarChart3 className="w-4 h-4" />}
          label="Stats"
          onClick={onStatsClick}
          ariaLabel="Open statistics modal"
        />
        <MobileToolbeltButton
          icon={<MessageCircle className="w-4 h-4" />}
          label="Discussion"
          onClick={onDiscussionClick}
          ariaLabel="Open discussion modal"
        />
        {shouldShowPremiumButton && (
          <MobileToolbeltButton
            icon={<Crown className="w-4 h-4 text-amber-500 dark:text-amber-400" />}
            label="Member"
            onClick={onPremiumClick}
            badge={undefined}
            badgeVariant="purple"
            notificationDot={showMemberNotificationDot}
            ariaLabel="Open member modal"
          />
        )}
      </div>
    </>
  );
};

export default DesktopToolbelt;
